// --- Web Components Implementation (using Liquid markup structure) ---

// Quantity Picker Web Component (no shadow DOM)
class QuantityPicker extends HTMLElement {
  constructor () {
    super()
    this.input = null
    this.incBtn = null
    this.decBtn = null
    this.max = null
    this.maxMsg = null
    this.quantityVal = null
  }

  connectedCallback () {
    this.input = this.querySelector('.input-number')
    this.incBtn = this.querySelector('.input-number-increment')
    this.decBtn = this.querySelector('.input-number-decrement')
    this.quantityVal = this.querySelector('.quantity_val')
    this.max = parseInt(this.input.getAttribute('max')) || null
    this.maxMsg = this.querySelector('.max-msg') || this.createMaxMsg()
    this.incBtn.addEventListener('click', () => this.changeQuantity(1))
    this.decBtn.addEventListener('click', () => this.changeQuantity(-1))
    this.input.addEventListener('input', () => this.onInputChange())
    this.addMaxMsgHandler()
  }

  setMax (val) {
    this.max = val
    this.input.setAttribute('max', val)
    if (parseInt(this.input.value) > val) {
      this.input.value = val
      this.dispatchChange()
    }
  }

  enableInputs () {
    this.input.removeAttribute('disabled')
    this.incBtn.removeAttribute('disabled')
    this.decBtn.removeAttribute('disabled')
  }

  disableInputs () {
    this.input.setAttribute('disabled', true)
    this.incBtn.setAttribute('disabled', true)
    this.decBtn.setAttribute('disabled', true)
  }

  changeQuantity (delta) {
    let val = parseInt(this.input.value) || 1
    val += delta
    if (val < 1) val = 1
    if (this.max && val > this.max) val = this.max
    this.input.value = val
    this.quantityVal.textContent = val
    this.dispatchChange()
    this.checkMaxMsg()
  }

  onInputChange () {
    let val = parseInt(this.input.value) || 1
    if (this.max && val > this.max) val = this.max
    if (val < 1) val = 1
    this.input.value = val
    this.quantityVal.textContent = val
    this.dispatchChange()
    this.checkMaxMsg()
  }

  dispatchChange () {
    this.dispatchEvent(
      new CustomEvent('quantity-change', {
        detail: { quantity: parseInt(this.input.value) },
        bubbles: true
      })
    )
  }

  createMaxMsg () {
    const msg = document.createElement('span')
    msg.classList.add('max-msg')
    msg.style.display = 'none'
    this.appendChild(msg)
    return msg
  }

  addMaxMsgHandler () {
    ;[this.incBtn, this.decBtn].forEach(btn => {
      btn.addEventListener('click', () => this.checkMaxMsg())
    })
  }

  checkMaxMsg () {
    if (this.max && parseInt(this.input.value) >= this.max) {
      this.maxMsg.innerText = `Max limit reached: ${this.max}`
      this.maxMsg.style.display = 'inline-block'
    } else {
      this.maxMsg.style.display = 'none'
    }
  }
}

if (!customElements.get('quantity-picker')) {
  customElements.define('quantity-picker', QuantityPicker)
}

// Variant Picker Web Component (no shadow DOM)
class VariantPicker extends HTMLElement {
  constructor () {
    super()
    this.product = null
    this.moneyFormat = null
    this.form = null
    this.wrapper = null
    this.hiddenInput = null
    this.variantInputField
  }

  async connectedCallback () {
    // Only activate if inside product-details or product-card
    this.wrapper =
      this.closest('product-details') || this.closest('product-card')
    if (!this.wrapper) return

    this.variantInputField = this.querySelector('.single_options')

    // Get product object from parent wrapper
    let productJson = this.wrapper.querySelector('.product-json')?.textContent
    try {
      this.product = JSON.parse(productJson)
    } catch (e) {
      this.product = productJson // fallback if not JSON
    }

    // If product is not an object, fetch product data
    if (typeof this.product !== 'object' || !this.product.variants) {
      const handle = typeof this.product === 'string' ? this.product : ''
      if (handle) {
        try {
          const res = await fetch(
            `${window.Shopify.routes.root}products/${handle}.js`
          )
           if (res.ok) {
             this.product = await res.json()
           } else {
             return
           }
         } catch (err) {
           return
         }
       } else {
         return
       }
    }

    this.moneyFormat = this.dataset.moneyFormat
    this.form = this.closest('form')
    this.hiddenInput = this.form?.querySelector('input[name="id"]')
     if (!this.product || !this.moneyFormat || !this.form) {
       return
     }

    if (this.product.variants?.length) {
      let selectValue = this.querySelector('.product-variant-select').value
      const defaultVariant =
        this.product.variants.find(variant => variant.id == selectValue) ||
        this.product.variants[0]
      if (this.hiddenInput) this.hiddenInput.value = defaultVariant.id
      this.handleVariantChange(defaultVariant)
      this.updateUI(defaultVariant)
      if (this.variantInputField) {
        this.updateVariantStatuses(
          this.product.variants,
          this.variantInputField
        )
      }
    }

    this.querySelectorAll(
      'select.product-variant-dropdown,.product_options input[type=radio]'
    ).forEach(el => {
      el.addEventListener('change', e => {
        if (e.target.matches('select, input[type=radio]')) {
          this.handleVariantChange()
        }
      })
    })
    this.addKeyboardAccessibility()
  }

  handleVariantChange (variant) {
    const selectedOptions = this.getSelectedOptions()
    if (!this.product) return
    let matchedVariant
    if (variant) {
      matchedVariant = variant
    } else {
      matchedVariant = this.product.variants.find(variant => {
        return variant.options.every((opt, i) => selectedOptions[i] === opt)
      })
    }

    this.dispatchEvent(
      new CustomEvent('variant-change', {
        detail: { variant: matchedVariant ? matchedVariant : undefined },
        bubbles: true
      })
    )

    if (matchedVariant) {
      this.hiddenInput.value = matchedVariant.id
      this.updateUI(matchedVariant)

      // Slide the single-product-swiper to the first slide
      const swiperEl = this.wrapper.querySelector('.single-product-swiper')
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.slideTo(0)
      }

      // 🔹 Run availability update logic after every variant change
      if (this.variantInputField) {
        this.updateVariantStatuses(
          this.product.variants,
          this.variantInputField
        )
      }
    }
  }

  getSelectedOptions () {
    const options = []
    this.querySelectorAll(
      'input[type=radio]:checked, select.product-variant-dropdown'
    ).forEach(el => {
      options.push(el.value)
    })
    return options
  }

  updateUI (variant) {
    this.form
      .querySelectorAll('.product_option > *')
      .forEach((field, index) => {
        let selectedVal = field.querySelector('.selected_value')
        if (selectedVal) {
          switch (index) {
            case 0:
              selectedVal.textContent = variant.option1
              break
            case 1:
              selectedVal.textContent = variant.option2
              break
            case 2:
              selectedVal.textContent = variant.option3
              break

            default:
              break
          }
        }
      })
    // Update price, compare price, unit price, SKU, etc.
    this.form.querySelectorAll('.product-price').forEach(price => {
      price.textContent = newFormatMoney(variant.price)
    })
    this.form.querySelectorAll('.product-compare-price').forEach(price => {
      variant.compare_at_price !== null
        ? (price.textContent = newFormatMoney(variant.compare_at_price))
        : (price.textContent = '')
    })
    this.form.querySelectorAll('.unit-price').forEach(price => {
      price.textContent = newFormatMoney(variant.unit_price)
    })
    this.form
      .querySelectorAll('.productUnitPriceMeasurement')
      .forEach(price => {
        price.textContent = variant.unit_price_measurement.reference_value
      })
    this.form.querySelectorAll('.productReferenceUnit').forEach(price => {
      price.textContent = variant.unit_price_measurement.reference_unit
    })
    this.form.querySelectorAll('.product-sku').forEach(sku => {
      sku.textContent = variant.sku
    })

    // Sale tag
    const saleTags = this.form.querySelectorAll('.addSaleTag')
    saleTags.forEach(saleTag => {
      if (
        variant.compare_at_price > variant.price &&
        variant.inventory_quantity > 0
      ) {
        saleTag.textContent = 'Sale'
      } else {
        saleTag.textContent = 'Sold Out'
      }
    })

    // Pickup availability
    let pickupEl = this.wrapper.querySelector('pickup-availability')
    let pickupVariant = this.wrapper.querySelector(
      '.pickup-availability-variant'
    )
    if (pickupVariant) pickupVariant.innerHTML = variant.title
    if (pickupEl) pickupEl.setAttribute('data-variant-id', variant.id)

    // Variant image
    if (variant.featured_image) {
      const swiper = this.wrapper.querySelector('.single-product-swiper')
      if (swiper) {
        const firstSlide = swiper.querySelector('.swiper-slide')
        if (firstSlide) {
          const img = firstSlide.querySelector('img')
          if (img) {
            img.setAttribute('src', variant.featured_image.src)
            img.setAttribute('alt', variant.featured_image.alt)
            img.setAttribute('loading', 'lazy')
            img.removeAttribute('srcset')
          }
        }
      } else {
        let cardImg = this.wrapper.querySelector('.product__image')
        if (cardImg) {
          cardImg.setAttribute('src', variant.featured_image.src)
          cardImg.setAttribute('alt', variant.featured_image.alt)
          cardImg.style.zIndex = 40
          cardImg.style.opacity = 1
          cardImg.removeAttribute('srcset')
        }
      }
    }

    // Update URL
    if (typeof URLParse !== 'undefined') {
      let url = new URLParse(window.location.href, true)
      url.query.variant = variant.id
      window.history.replaceState(null, null, url.toString())
    }

    // Hide unavailable variants
    this.hideUnavailableVariants()
  }

  hideUnavailableVariants () {
    // This logic is adapted from the old VariantAvailability web component
    let availableVariants = this.form.querySelectorAll(
      '.product-variant-select option.available'
    )
    let newOption1 = [],
      newOption2 = [],
      newOption3 = []
    availableVariants.forEach(option => {
      let optionVal = option.value
      let newVariant = this.product.variants.find(
        variant => variant.id == optionVal
      )
      newOption1.push(newVariant.option1)
      newOption2.push(newVariant.option2)
      newOption3.push(newVariant.option3)
    })
    let option1 = this.querySelector(
      ".single_options > fieldset[data-option='1']"
    )
    let option2 = this.querySelector(
      ".single_options > fieldset[data-option='2']"
    )
    let option3 = this.querySelector(
      ".single_options > fieldset[data-option='3']"
    )
    if (option1) {
      let values1 = option1.querySelectorAll('.product-variant-option')
      values1.forEach(val => {
        newOption1.forEach(newVal => {
          if (val.querySelector("input[type='radio']").value == newVal) {
            val.classList.add('hasStock')
          }
        })
      })
    }
    if (option2) {
      let values2 = option2.querySelectorAll('.product-variant-option')
      values2.forEach(val => {
        newOption2.forEach(newVal => {
          if (val.querySelector("input[type='radio']").value == newVal) {
            val.classList.add('hasStock')
          }
        })
      })
    }
    if (option3) {
      let values3 = option3.querySelectorAll('.product-variant-option')
      values3.forEach(val => {
        newOption3.forEach(newVal => {
          if (val.querySelector("input[type='radio']").value == newVal) {
            val.classList.add('hasStock')
          }
        })
      })
    }
  }

  addKeyboardAccessibility () {
    this.querySelectorAll('.product-variant-option').forEach(option => {
      option.addEventListener('keyup', e => {
        if (e.key == 'Enter' || e.key == ' ') {
          option.querySelector('input').click()
        }
      })
    })
  }

  // 🔹 Added from your snippet
  updateVariantStatuses (variants, inputField) {
    const selectedOptionOneVariants = variants.filter(
      variant => inputField.querySelector(':checked')?.value === variant.option1
    )

    const inputWrappers = [
      ...inputField.querySelectorAll('.single_options fieldset')
    ]
    inputWrappers.forEach((option, index) => {
      if (index === 0) return
      const optionInputs = [
        ...option.querySelectorAll('input[type="radio"], option')
      ]
      const previousOptionSelected =
        inputWrappers[index - 1].querySelector(':checked')?.value
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(
          variant =>
            variant.available &&
            variant[`option${index}`] === previousOptionSelected
        )
        .map(variantOption => variantOption[`option${index + 1}`])

      this.setInputAvailability(optionInputs, availableOptionInputsValue)
    })
  }

  setInputAvailability (listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value')
        input
          .closest('.product-variant-option')
          .querySelector('label')
          .classList.remove('disabled')
      } else {
        input
          .closest('.product-variant-option')
          .querySelector('label')
          .classList.add('disabled')
      }
    })
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker)
}

// --- Buy Buttons Web Component (patched) ---
class BuyButtons extends HTMLElement {
  constructor () {
    super()
    this.variant = null
    this.quantity = 1
    this.maxQuantity = null
    this.form = null
    this.product = null
    this.matchVariantOption = null
  }

  connectedCallback () {
    this.form = this.closest('form')
    const wrapper =
      this.closest('product-details') ||
      this.closest('product-card') || this.closest("quick-view")
    if (!wrapper || !this.form) return

    // Buttons + text/spinner fallbacks
    this.addBtn = this.querySelector('.product-add')
    this.buyBtn = this.querySelector('.product-buy')
    this.addBtnText = this.addBtn?.querySelector('.text, .btn-text') || null
    this.buyBtnText = this.buyBtn?.querySelector('.text, .btn-text') || null
    this.addSpinner = this.addBtn?.querySelector('.spinner') || null

    // Prevent native submit racing our JS
    this.addBtn?.setAttribute('type', 'button')
    this.buyBtn?.setAttribute('type', 'button')

    // Hidden variant id + qty input fallbacks
    this.idInput = this.form.querySelector('input[name="id"]')
    this.qtyInput = this.form.querySelector('input.quantity')

    // Listen for bubbling events in scope that will actually receive them
    // Quantity-change bubbles to the common FORM ancestor
    this.form.addEventListener('quantity-change', e => {
      this.quantity = Math.max(1, parseInt(e.detail.quantity || '1', 10))
    })

    // Fallback to input if user types directly
    if (this.qtyInput) {
      this.quantity = Math.max(1, parseInt(this.qtyInput.value || '1', 10))
      this.qtyInput.addEventListener('input', () => {
        this.quantity = Math.max(1, parseInt(this.qtyInput.value || '1', 10))
      })
    }

    // Variant changes can come from product pages OR quick view (we dispatch from quickview.js)
    document.addEventListener('variant-change', e => this.onVariantChange(e))

    // Clicks
    this.addBtn?.addEventListener('click', e => {
      this.addToCart(e)
    })
    this.buyBtn?.addEventListener('click', e => this.buyNow(e))

    // Initial state: try to derive variant from hidden input
    this.hydrateVariantFromHidden()
    this.updateButtons()

    // If we have product JSON nearby, set initial max for qty picker
    let product = null
    try {
      product = JSON.parse(wrapper.querySelector('.product-json')?.textContent) || fetchProduct()
      this.product = product
    } catch (e) {
      product = null
    }

    function fetchProduct() {
      const handle = wrapper.dataset.productHandle || ''
      if (handle) {
         return fetch(`${window.Shopify.routes.root}products/${handle}.js`)
           .then(res => res.ok ? res.json() : null)
           .catch(err => {
             return null
           })
      }
      return null
    }

    if (product?.variants?.length) {
      const initialVariant =
        product.variants.find(variant => variant.id == this.idInput.value) ||
        product.variants[0]
      if (initialVariant) {
        this.variant = initialVariant
        this.updateButtons()
        const quantityPicker = this.form.querySelector('quantity-picker')
        if (quantityPicker && this.variant) {
          let variantId = this.variant.id
          this.matchVariantOption = Array.from(
            this.form.querySelectorAll('.product-variant-select option')
          ).find(option => option.value == variantId)
          if (this.matchVariantOption) {
            let quantity = this.matchVariantOption.getAttribute('data-quantity')
            this.maxQuantity = quantity
          }
          quantityPicker.setMax(this.maxQuantity)
        }
      }
    }
  }

  hydrateVariantFromHidden () {
    const id = this.idInput?.value
    if (id) {
      // Minimal variant snapshot; availability will be kept in sync by quickview.js _setStock
      const disabled = this.addBtn?.disabled || this.buyBtn?.disabled
      this.variant = { id, available: !disabled }
    }
  }

  onVariantChange (e) {
    const v = e.detail?.variant
    const quantityPicker = this.form.querySelector('quantity-picker')
    if (!v) {
      this.variant = undefined
      this.quantity = 0
      quantityPicker.setMax(this.quantity)
      this.updateButtons()
      quantityPicker.disableInputs()
      return
    }
    this.variant = v
    if (this.idInput) this.idInput.value = v.id
    // Update qty picker max if present
    if (quantityPicker) {
      let variantId = v.id
      this.matchVariantOption = Array.from(
        this.form.querySelectorAll('.product-variant-select option')
      ).find(option => option.value == variantId)

      if (this.matchVariantOption) {
        let quantity = this.matchVariantOption.getAttribute('data-quantity')
        this.maxQuantity = quantity
      }
      if (v.available) {
        if (this.qtyInput) this.qtyInput.value = 1
        quantityPicker.enableInputs()
      } else {
        quantityPicker.disableInputs()
      }
      quantityPicker.setMax(this.maxQuantity || 0)
    }
    this.updateButtons()
  }

  updateButtons () {
    if (!this.addBtn || !this.buyBtn) return
    const available = !!this.variant?.available
    if (this.addBtnText)
      this.addBtnText.textContent = available ? 'Add to Cart' : 'Out of Stock'
    if (this.buyBtnText)
      this.buyBtnText.textContent = available ? 'Buy Now' : 'Out of Stock'
    this.addBtn.disabled = !available
    this.buyBtn.disabled = !available
  }

  async addToCart (e) {
    e.preventDefault()

    let id

    if (this.dataset.variantId) {
      id = this.dataset.variantId
    } else {
      id = this.idInput?.value || this.variant?.id
    }

    if (!id) return

    if (this.addSpinner) this.addSpinner.style.display = 'inline-flex'
    const qty = Math.max(
      1,
      this.quantity || parseInt(this.qtyInput?.value || '1', 10)
    )
    
    await fetch('/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ id, quantity: qty })
    })

    const res = await fetch('/cart.json')
    const cart = await res.json()

    if (cart && cart.item_count > 0) {
      document.querySelectorAll('.cart__count').forEach(el => {
        el.classList.add('show_cart')
      })

      //  updating cart drawer
      const cartRes = await fetch(
        window.location.pathname + '?section_id=cart-drawer'
      )
      const newCart = await cartRes.text()
      let cartDrawer = document.querySelector('#cartDrawer')
      if (cartDrawer) {
        let html = document.createElement('div')
        html.innerHTML = newCart
        cartDrawer.querySelector('.cart-drawer-inner').innerHTML =
          html.querySelector('#cartDrawer .cart-drawer-inner').innerHTML

        const qv = document.querySelector('#quickView')
        if (qv) {
          document.documentElement.classList.remove('overflow-hidden')
          qv.classList.remove('quickview_active')
        }
        let drawers = document.querySelectorAll('cart-drawer')
        if (drawers) {
          drawers.forEach(el => {
            el.init?.()
          })
        }
        cartDrawer.classList.add('drawer-active')
      }
    }

    if (this.addSpinner) this.addSpinner.style.display = 'none'
  }

  buyNow (e) {
    e.preventDefault()

    const id =
      (this.variant && this.variant.id) || (this.idInput && this.idInput.value)

    if (!id) return

    const qty = Math.max(
      1,
      this.quantity || parseInt(this?.qtyInput?.value || '1', 10) || 1
    )

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/cart/add'
    form.style.display = 'none'

    // required
    form.innerHTML = `
    <input type="hidden" name="id" value="${id}">
    <input type="hidden" name="quantity" value="${qty}">
    <input type="hidden" name="return_to" value="/checkout">
  `

    document.body.appendChild(form)
    form.submit() // more reliable than programmatic button click
  }
}

if (!customElements.get('buy-buttons')) {
  customElements.define('buy-buttons', BuyButtons)
}

// Product Details Web Component
class ProductDetails extends HTMLElement {
   constructor () {
     super()
     this.product = null
   }

   connectedCallback () {
     try {
       this.product = JSON.parse(this.querySelector('.product-json').textContent)
     } catch (e) {
       // Error handling omitted
     }
    if (!this.product) return
  }
}

if (!customElements.get('product-details')) {
  customElements.define('product-details', ProductDetails)
}

// Product Card Web Component
class ProductCard extends HTMLElement {
   constructor () {
     super()
     this.product = null
   }

   connectedCallback () {
     try {
       this.product = JSON.parse(this.querySelector('.product-json').textContent)
     } catch (e) {
       // Error handling omitted
     }
    if (!this.product) return
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
